import { ArdoqId, ReferenceDirection, ViewIds } from '@ardoq/api-types';
import { isTruthy, returnNull, WILDCARD } from '@ardoq/common-helpers';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import {
  DropdownHeader,
  DropdownItem,
  DropdownOption,
  DropdownOptionType,
  isDropdownOption,
} from '@ardoq/dropdown-menu';
import { IconName } from '@ardoq/icons';
import {
  DisabledZoomControls,
  DropdownConfig,
  isDropdownConfig,
  SettingsConfig,
  SettingsType,
  updateViewSettings,
  ViewContext,
} from '@ardoq/view-settings';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { isNil } from 'lodash';
import {
  BubbleChartQuadrantSettings,
  BubbleChartRenderingData,
  BubbleChartSelectedFieldType,
  BubbleChartViewSettings,
  ComponentTypeSelectLabels,
  FieldSource,
  FieldInfoKey,
  FieldNameAndLabel,
} from '../types';
import { dispatchAction } from '@ardoq/rxbeach';
import { Locale, localeCompareNumericLowercase } from '@ardoq/locale';
import { UpdatePersistedViewLegendSettingsArgs } from '@ardoq/view-legend';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import type { ComponentTypesById } from '../settingsDependencies';
import { getRedactedFieldDisplay } from '@ardoq/renderers';

export type { ComponentTypeSelectLabels };

export const updateSelectedQuadrantViewSettings = (
  prev: BubbleChartRenderingData,
  current: BubbleChartRenderingData
): void => {
  const { isPresentationMode, quadrantZoomState } = current;

  if (isPresentationMode) {
    return;
  }

  const { selectedQuadrant } = quadrantZoomState;
  const isSelectedQuadrantChanged =
    selectedQuadrant !== prev.quadrantZoomState.selectedQuadrant;

  if (isSelectedQuadrantChanged) {
    dispatchAction(
      updateViewSettings({
        viewId: ViewIds.BUBBLE,
        settings: { selectedQuadrant },
        persistent: true,
      })
    );
  }
};

const DEFAULT_COMPONENT_TYPE_SELECT_LABELS: ComponentTypeSelectLabels = {
  allLabel: 'All',
  componentTypeLabel: 'Component type',
};

export type AvailableReferenceFieldOptionsLabels = {
  referenceFieldsHeader: string;
  referencedComponentFieldsHeader: string;
  bubbleComponentFieldsHeader: string;
};

const DEFAULT_AVAILABLE_REFERENCE_FIELD_OPTIONS_LABELS: AvailableReferenceFieldOptionsLabels =
  {
    referenceFieldsHeader: 'Reference fields',
    referencedComponentFieldsHeader: 'Referenced Component fields',
    bubbleComponentFieldsHeader: 'Source of bubbles fields',
  };

type GetComponentTypeOptionsArgsBase = {
  componentTypesById: ComponentTypesById[];
  selectedComponentTypes: ArdoqId[];
  onViewSettingsUpdate: OnViewSettingsUpdate;
  fieldSource?: BubbleChartViewSettings['fieldSource'];
};

type GetComponentTypeOptionsArgs = GetComponentTypeOptionsArgsBase & {
  labels: ComponentTypeSelectLabels;
};

type GetComponentTypeSelectConfigArgs = GetComponentTypeOptionsArgsBase & {
  labels?: Partial<ComponentTypeSelectLabels>;
};

/**
 * Generates component type options for viewpoint mode.
 * Selecting a component type switches back to component mode from reference mode.
 */
const getComponentTypeOptions = ({
  componentTypesById,
  selectedComponentTypes,
  onViewSettingsUpdate,
  fieldSource = FieldSource.BY_COMPONENT,
  labels,
}: GetComponentTypeOptionsArgs): DropdownOption[] => {
  const isByComponent = fieldSource === FieldSource.BY_COMPONENT;

  const applySelection = (selectedComponentTypes: ArdoqId[]) => {
    const settings = {
      selectedComponentTypes,
      fieldSource: FieldSource.BY_COMPONENT,
    };

    onViewSettingsUpdate(ViewIds.BUBBLE, settings, true);
  };

  return [
    {
      name: WILDCARD,
      label: labels.allLabel,
      isActive: isByComponent && selectedComponentTypes.includes(WILDCARD),
      type: DropdownOptionType.OPTION,
      onClick: () => applySelection([WILDCARD]),
    },
    ...componentTypesById.map(({ name, ids }) => ({
      name: ids[0],
      label: name,
      isActive:
        isByComponent && ids.some(id => selectedComponentTypes.includes(id)),
      type: DropdownOptionType.OPTION,
      onClick: () => applySelection(ids),
    })),
  ];
};

export const getComponentTypeSelectConfig = ({
  componentTypesById,
  selectedComponentTypes,
  onViewSettingsUpdate,
  fieldSource = FieldSource.BY_COMPONENT,
  labels: labelsProp,
}: GetComponentTypeSelectConfigArgs): DropdownConfig => {
  const labels: ComponentTypeSelectLabels = {
    ...DEFAULT_COMPONENT_TYPE_SELECT_LABELS,
    ...labelsProp,
  };
  return {
    id: 'selectedComponentTypes',
    label: labels.componentTypeLabel,
    iconName: IconName.COMPONENT,
    options: getComponentTypeOptions({
      componentTypesById,
      selectedComponentTypes,
      onViewSettingsUpdate,
      fieldSource,
      labels,
    }),
    type: SettingsType.DROPDOWN,
    isDisabled: false,
    maxDropdownSize: DropdownSize.M,
  };
};

type GetUpdatedFieldValueArgs = {
  isActive: boolean;
  resetFieldOnDeselect: boolean;
  name: string;
  type: BubbleChartSelectedFieldType;
};

/**
 * Returns the updated field value based on the current selection state and mode.
 * Workspace mode uses a sentinel object on deselect; viewpoint mode clears the field to an empty string.
 */
const getUpdatedFieldValue = ({
  isActive,
  resetFieldOnDeselect,
  name,
  type,
}: GetUpdatedFieldValueArgs) => {
  if (!isActive) {
    return { fieldName: name, type };
  }

  if (resetFieldOnDeselect) {
    return '';
  }

  return { fieldName: name, type: undefined };
};

const referenceFieldNameOption = (
  { name, label, isConfidential }: FieldNameAndLabel,
  currentSettings: BubbleChartViewSettings,
  key: FieldInfoKey,
  type: BubbleChartSelectedFieldType,
  shouldClearLabelFilter = false,
  resetFieldOnDeselect = false
) => {
  const value = currentSettings[key];
  const isActive =
    typeof value === 'object' &&
    value?.fieldName === name &&
    value?.type === type;

  return {
    label,
    type: DropdownOptionType.OPTION,
    isActive,
    isDisabled: isConfidential,
    rightContent: isConfidential ? getRedactedFieldDisplay() : undefined,
    onClick: () =>
      dispatchAction(
        updateViewSettings({
          viewId: ViewIds.BUBBLE,
          settings: {
            [key]: getUpdatedFieldValue({
              isActive,
              resetFieldOnDeselect,
              name,
              type,
            }),
            ...(shouldClearLabelFilter && { labelFilter: undefined }),
          } satisfies Partial<BubbleChartViewSettings>,
          persistent: true,
        })
      ),
  };
};

type ReferenceTypeListLabels = {
  incomingHeader: string;
  outgoingHeader: string;
};

type ToReferenceTypeOptionListArgs = {
  availableIncomingReferenceTypeNames: string[];
  availableOutgoingReferenceTypeNames: string[];
  labels: ReferenceTypeListLabels;
  toOption: (
    referenceTypeName: string,
    direction: ReferenceDirection
  ) => DropdownOption;
};

export const toReferenceTypeOptionList = ({
  availableIncomingReferenceTypeNames,
  availableOutgoingReferenceTypeNames,
  labels,
  toOption,
}: ToReferenceTypeOptionListArgs): DropdownItem[] =>
  [
    availableIncomingReferenceTypeNames.length > 0
      ? { type: DropdownOptionType.HEADER, label: labels.incomingHeader }
      : null,
    ...availableIncomingReferenceTypeNames.map(referenceTypeName =>
      toOption(referenceTypeName, ReferenceDirection.INCOMING)
    ),
    availableIncomingReferenceTypeNames.length > 0 &&
    availableOutgoingReferenceTypeNames.length > 0
      ? { id: 'divider', type: DropdownOptionType.DIVIDER }
      : null,
    availableOutgoingReferenceTypeNames.length > 0
      ? { type: DropdownOptionType.HEADER, label: labels.outgoingHeader }
      : null,
    ...availableOutgoingReferenceTypeNames.map(referenceTypeName =>
      toOption(referenceTypeName, ReferenceDirection.OUTGOING)
    ),
  ].filter(isTruthy) as DropdownItem[];

export const referenceTypeNameOption = (
  referenceTypeName: string,
  direction: ReferenceDirection,
  isActive: boolean
): DropdownOption => ({
  name: referenceTypeName,
  type: DropdownOptionType.OPTION,
  label: referenceTypeName,
  isActive,
  onClick: () => {
    const settings = {
      fieldSource: FieldSource.BY_REFERENCE,
      selectedReferenceDirection: direction,
      selectedReferenceTypeName: referenceTypeName,
    };

    dispatchAction(
      updateViewSettings({
        viewId: ViewIds.BUBBLE,
        settings: settings satisfies Partial<BubbleChartViewSettings>,
        persistent: true,
      })
    );
  },
});

type AvailableReferenceFieldOptionsArgs = {
  availableBubbleComponentFields: FieldNameAndLabel[];
  availableReferenceFields: FieldNameAndLabel[];
  availableReferencedComponentFields: FieldNameAndLabel[];
  currentSettings: BubbleChartViewSettings;
  key: FieldInfoKey;
  locale: Locale;
  shouldClearLabelFilter: boolean;
  labelsProp: Partial<AvailableReferenceFieldOptionsLabels>;
  resetFieldOnDeselect: boolean;
};

export const availableReferenceFieldOptions = ({
  availableBubbleComponentFields,
  availableReferenceFields,
  availableReferencedComponentFields,
  currentSettings,
  key,
  locale,
  shouldClearLabelFilter,
  labelsProp,
  resetFieldOnDeselect,
}: AvailableReferenceFieldOptionsArgs): DropdownItem[] => {
  const labels: AvailableReferenceFieldOptionsLabels = {
    ...DEFAULT_AVAILABLE_REFERENCE_FIELD_OPTIONS_LABELS,
    ...labelsProp,
  };
  const fieldsToOptions = (
    referenceFields: FieldNameAndLabel[],
    type: BubbleChartSelectedFieldType
  ) => {
    return referenceFields
      .sort((a, b) => localeCompareNumericLowercase(a.label, b.label, locale))
      .map(referenceField =>
        referenceFieldNameOption(
          referenceField,
          currentSettings,
          key,
          type,
          shouldClearLabelFilter,
          resetFieldOnDeselect
        )
      );
  };
  const sections = [
    {
      fields: availableBubbleComponentFields,
      header: labels.bubbleComponentFieldsHeader,
      type: BubbleChartSelectedFieldType.BUBBLE_COMPONENT,
    },
    {
      fields: availableReferenceFields,
      header: labels.referenceFieldsHeader,
      type: BubbleChartSelectedFieldType.REFERENCE,
    },
    {
      fields: availableReferencedComponentFields,
      header: labels.referencedComponentFieldsHeader,
      type: BubbleChartSelectedFieldType.REFERENCED_COMPONENT,
    },
  ].filter(({ fields }) => fields.length > 0);

  return sections.flatMap(({ fields, header, type }, index) => [
    ...(index > 0 ? [{ type: DropdownOptionType.DIVIDER }] : []),
    { type: DropdownOptionType.HEADER, label: header } satisfies DropdownHeader,
    ...fieldsToOptions(fields, type),
  ]);
};

type GetBubbleChartViewContextArgs = {
  getBubbleChartContentContainer: () => HTMLDivElement | null;
  isLegendActive: boolean;
  zoomControls: SettingsConfig[] | null;
  viewInstanceId: string;
  viewLegendCommands: {
    updateViewLegendSettings: ({
      viewId,
      isActive,
      persistent,
    }: UpdatePersistedViewLegendSettingsArgs) => void;
  };
};
export const getBubbleChartViewContext = ({
  getBubbleChartContentContainer,
  isLegendActive,
  zoomControls,
  viewInstanceId,
  viewLegendCommands,
}: GetBubbleChartViewContextArgs): ViewContext => ({
  getContainer: getBubbleChartContentContainer,
  getGraphComponent: returnNull,
  zoomIn: null,
  zoomOut: null,
  optimizeLayout: null,
  toggleLegend: () =>
    viewLegendCommands.updateViewLegendSettings({
      viewId: ViewIds.BUBBLE,
      isActive: !isLegendActive,
    }),
  isLegendActive,
  zoomControls,
  zoomControlsDisabledState: DisabledZoomControls.NONE,
  viewInstanceId,
  knowledgeBaseLink: KnowledgeBaseLink.BUBBLE_CHART,
});

export const findActiveDropdownOptionTagLabel = (
  viewModifier: SettingsConfig
): string => {
  if (!isDropdownConfig(viewModifier)) {
    return '-';
  }

  const label = viewModifier.options
    .filter(isDropdownOption)
    .find(option => option.isActive || option.isSelected)?.label;

  if (typeof label === 'string') {
    return label;
  }

  if (typeof label === 'number') {
    return String(label);
  }

  // At this moment, we aren't supposed to pass anything other
  // than string or number to the tag labels

  return '-';
};

const isAutoValue = (value: number): boolean => isNil(value) || isNaN(value);

export const hasCustomLayoutLimits = (
  customQuadrantSettings: BubbleChartQuadrantSettings | null
): boolean => {
  if (!customQuadrantSettings) {
    return false;
  }

  const { xMinimum, xMaximum, yMinimum, yMaximum } = customQuadrantSettings;

  return ![xMinimum, xMaximum, yMinimum, yMaximum].every(isAutoValue);
};
